
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Maximize2, Minimize2, Map as MapIcon, ChevronLeft, ArrowUp, ArrowRight, ArrowLeft, RotateCw, Zap, XCircle, Eye } from 'lucide-react';
import { Order, SystemConfig } from '../../types';
import { loadGoogleMaps, checkGoogleMapsAvailability, onGoogleMapsAuthFailure } from '../../services/googleMapsLoader';
import { CLEAN_MAP_STYLES } from '../../constants';
import { calculateDistance } from '../../services/parserService';

declare var google: any;

interface Props {
    config: SystemConfig;
    driverLat?: number;
    driverLng?: number;
    activeOrders: Order[];
    nextOrder: Order | null;
    isStationaryAtHub: boolean;
    onEtaUpdate: (minutes: number) => void;
    mapProvider: 'loading' | 'google' | 'leaflet';
    setMapProvider: (provider: 'loading' | 'google' | 'leaflet') => void;
    displayMode?: 'tactical' | 'recon';
    setDisplayMode?: (mode: 'tactical' | 'recon') => void;
    isNavigatingInternal?: boolean;
    onStopInternalNav?: () => void;
    avgSpeedKmH?: number; // v6.3: Para cálculo local de ETA
    speedFactor?: number;  // v6.3: Para cálculo local de ETA
}

export const DriverMap: React.FC<Props> = ({
    config, driverLat, driverLng, activeOrders, nextOrder, isStationaryAtHub, onEtaUpdate, mapProvider, setMapProvider,
    displayMode = 'tactical', setDisplayMode, isNavigatingInternal = false, onStopInternalNav,
    avgSpeedKmH = 20, speedFactor = 1.0
}) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const panoContainerRef = useRef<HTMLDivElement>(null);
    const [isMapExpanded, setIsMapExpanded] = useState(false);

    const mapInstance = useRef<any | null>(null);
    const panoInstance = useRef<any | null>(null);
    const overlaysRef = useRef<{ [id: string]: any }>({});
    const polylinesRef = useRef<any[]>([]);
    const directionsService = useRef<any | null>(null);

    // v9.4: Cooldown de API (Zero-Waste Logic)
    // Aumentado para 2 minutos (120000ms) para parear com a regra de 250m de GPS.
    const lastApiCallRef = useRef<number>(0);
    const API_COOLDOWN_MS = 600000; // v6.3: Aumentado para 10 minutos (Focamos em Desvio de Rota)
    const referencePathRef = useRef<any[]>([]);
    const lastDestinationRef = useRef<string | null>(null);

    const [currentInstruction, setCurrentInstruction] = useState<any>(null);

    const getInstructionText = (instructionHtml: string): string => instructionHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    useEffect(() => {
        let isMounted = true;
        const unsubscribe = onGoogleMapsAuthFailure(() => {
            if (isMounted) {
                if (mapContainerRef.current) mapContainerRef.current.innerHTML = '';
                mapInstance.current = null;
                setMapProvider('leaflet');
            }
        });

        loadGoogleMaps(config.googleMapsApiKey).then(() => {
            if (!isMounted) return;
            if (checkGoogleMapsAvailability()) setMapProvider('google');
            else setMapProvider('leaflet');
        });

        return () => {
            isMounted = false;
            if (mapInstance.current && typeof mapInstance.current.remove === 'function') mapInstance.current.remove();
            unsubscribe();
        };
    }, [config.googleMapsApiKey, setMapProvider]);

    const createCustomMarkerClass = useCallback(() => {
        return class CustomMarker extends google.maps.OverlayView {
            position: any; container: HTMLDivElement;
            constructor(pos: any, content: string) {
                super(); this.position = pos; this.container = document.createElement('div');
                this.container.style.position = 'absolute'; this.container.innerHTML = content;
            }
            onAdd() { (this as any).getPanes()?.floatPane.appendChild(this.container); }
            onRemove() { this.container.parentElement?.removeChild(this.container); }
            draw() {
                const projection = (this as any).getProjection();
                if (!projection) return;
                const pos = projection.fromLatLngToDivPixel(this.position);
                if (pos) {
                    this.container.style.left = pos.x + 'px';
                    this.container.style.top = pos.y + 'px';
                }
            }
        };
    }, []);

    const updateMap = useCallback(async () => {
        if (document.hidden) return;

        if (!mapContainerRef.current || !checkGoogleMapsAvailability()) return;

        const currentLat = isStationaryAtHub ? config.businessLat : (driverLat || config.businessLat);
        const currentLng = isStationaryAtHub ? config.businessLng : (driverLng || config.businessLng);
        const driverLatLng = new google.maps.LatLng(currentLat, currentLng);

        if (mapProvider === 'google') {
            if (!mapInstance.current) {
                mapInstance.current = new google.maps.Map(mapContainerRef.current, {
                    zoom: 14,
                    center: driverLatLng,
                    disableDefaultUI: true,
                    styles: CLEAN_MAP_STYLES,
                    gestureHandling: 'greedy'
                });
                directionsService.current = new google.maps.DirectionsService();
            }

            mapInstance.current.setOptions({
                tilt: isNavigatingInternal ? 45 : 0,
                zoomControl: !isNavigatingInternal
            });

            // v9.3: Atualização visual dos marcadores (Custo Zero)
            const CustomMarker = createCustomMarkerClass();

            const driverId = "driver-pos";
            if (overlaysRef.current[driverId]) overlaysRef.current[driverId].setMap(null);

            const driverHtml = `
                <div style="transform: translate(-50%, -50%) rotate(0deg); transition: transform 0.2s;">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L4.5 20.29L5.21 21L12 18L18.79 21L19.5 20.29L12 2Z" fill="#06b6d4" stroke="white" stroke-width="2" stroke-linejoin="round"/>
                    </svg>
                </div>
            `;
            const dMarker = new CustomMarker(driverLatLng, driverHtml);
            dMarker.setMap(mapInstance.current);
            overlaysRef.current[driverId] = dMarker;

            const activeIds = new Set([driverId]);
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(driverLatLng);

            activeOrders.forEach((o, idx) => {
                const pos = new google.maps.LatLng(o.reconLat || o.lat, o.reconLng || o.lng);
                bounds.extend(pos);
                activeIds.add(o.id);
                if (overlaysRef.current[o.id]) overlaysRef.current[o.id].setMap(null);
                const isTarget = o.id === nextOrder?.id;
                const bgColor = isTarget ? '#ef4444' : '#475569';
                const zIndex = isTarget ? 50 : 10;
                const orderHtml = `
                    <div style="position: absolute; transform: translate(-50%, -50%); z-index: ${zIndex};">
                        <div style="width: 32px; height: 32px; background-color: ${bgColor}; border: 2px solid white; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 12px; box-shadow: 0 4px 8px rgba(0,0,0,0.3);">
                            ${idx + 1}
                        </div>
                    </div>
                `;
                const oMarker = new CustomMarker(pos, orderHtml);
                oMarker.setMap(mapInstance.current);
                overlaysRef.current[o.id] = oMarker;
            });

            // v6.3: Lógica de Otimização API-ZERO
            const now = Date.now();
            const destinationId = nextOrder?.id || 'none';
            const destinationChanged = lastDestinationRef.current !== destinationId;

            // 1. Detecção de Desvio (Panic Mode)
            let isOffRoute = false;
            if (referencePathRef.current.length > 0 && driverLat && driverLng && google.maps.geometry) {
                const driverPos = new google.maps.LatLng(driverLat, driverLng);
                // Verifica se está a mais de 500m da borda da polyline original
                isOffRoute = !google.maps.geometry.poly.isLocationOnEdge(driverPos, new google.maps.Polyline({ path: referencePathRef.current }), 0.005); // ~500m tolerance
            }

            const shouldRecalculateRoute = (polylinesRef.current.length === 0) || destinationChanged || isOffRoute;

            if (activeOrders.length > 0 && shouldRecalculateRoute && (now - lastApiCallRef.current > 30000)) { // Cooldown mínimo de 30s mesmo em desvio
                lastApiCallRef.current = now;
                lastDestinationRef.current = destinationId;

                polylinesRef.current.forEach((polyline) => {
                    polyline.setMap(null);
                });
                polylinesRef.current = [];

                const waypoints = activeOrders.slice(0, -1).map(o => ({
                    location: { lat: o.reconLat || o.lat, lng: o.reconLng || o.lng },
                    stopover: true
                }));

                directionsService.current.route({
                    origin: driverLatLng,
                    destination: {
                        lat: activeOrders[activeOrders.length - 1].reconLat || activeOrders[activeOrders.length - 1].lat,
                        lng: activeOrders[activeOrders.length - 1].reconLng || activeOrders[activeOrders.length - 1].lng
                    },
                    waypoints: waypoints,
                    travelMode: google.maps.TravelMode.DRIVING
                }, (result: any, status: any) => {
                    if (status === 'OK' && result.routes[0]) {
                        const polyline = new google.maps.Polyline({
                            path: result.routes[0].overview_path,
                            strokeColor: '#06b6d4',
                            strokeOpacity: 0.6,
                            strokeWeight: isNavigatingInternal ? 10 : 5,
                            map: mapInstance.current,
                            zIndex: 1
                        });
                        polylinesRef.current.push(polyline);
                        referencePathRef.current = result.routes[0].overview_path;

                        const leg = result.routes[0].legs[0];
                        onEtaUpdate(Math.ceil((leg.duration_in_traffic ? leg.duration_in_traffic.value : leg.duration.value) / 60));

                        if (leg.steps && leg.steps.length > 0) {
                            setCurrentInstruction(leg.steps[0]);
                        }
                    }
                });
            } else if (activeOrders.length > 0 && polylinesRef.current.length > 0 && nextOrder) {
                // v6.3: Interpolação Local (Custo Zero)
                const targetLat = nextOrder.reconLat || nextOrder.lat || 0;
                const targetLng = nextOrder.reconLng || nextOrder.lng || 0;
                const distLinear = (driverLat && driverLng) ? calculateDistance(driverLat, driverLng, targetLat, targetLng) : 0;
                const distRoad = distLinear * 1.28; // Estimativa de tortuosidade

                // ETA = (Distância / Velocidade Real) + Ajuste de Fator
                const effectiveSpeed = (avgSpeedKmH * speedFactor);
                const localEta = Math.ceil((distRoad / Math.max(5, effectiveSpeed)) * 60);

                onEtaUpdate(localEta);
            }

            if (isNavigatingInternal) {
                mapInstance.current.panTo(driverLatLng);
                if (mapInstance.current.getZoom() < 18) mapInstance.current.setZoom(18);
            } else {
                mapInstance.current.fitBounds(bounds, { padding: 60 });
            }
        }
    }, [mapProvider, driverLat, driverLng, activeOrders, nextOrder, config, isStationaryAtHub, isNavigatingInternal, onEtaUpdate, createCustomMarkerClass, avgSpeedKmH, speedFactor]);

    useEffect(() => {
        updateMap();
        const handleVisibilityChange = () => { if (!document.hidden) updateMap(); };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [updateMap]);

    const getInstructionIcon = (instruction: string) => {
        const text = instruction.toLowerCase();
        if (text.includes('direita')) return <ArrowRight className="w-6 h-6" />;
        if (text.includes('esquerda')) return <ArrowLeft className="w-6 h-6" />;
        if (text.includes('retorno') || text.includes('u-turn')) return <RotateCw className="w-6 h-6" />;
        return <ArrowUp className="w-6 h-6" />;
    };

    return (
        <div className="relative max-w-3xl mx-auto">
            <motion.div
                animate={{ height: isNavigatingInternal ? '85vh' : isMapExpanded ? '65vh' : '14rem' }}
                className="w-full bg-card-bg border-b border-border-dim relative z-10 shadow-inner overflow-hidden"
            >
                <div ref={mapContainerRef} className="absolute inset-0 z-0" />
                <div ref={panoContainerRef} className={`absolute inset-0 z-[25] transition-opacity duration-500 ${displayMode === 'recon' ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} />
                <AnimatePresence>
                    {isNavigatingInternal && currentInstruction && displayMode === 'tactical' && (
                        <motion.div initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -100, opacity: 0 }} className="absolute top-4 left-4 right-4 z-30 bg-card-bg/95 backdrop-blur-md border border-sal-cyber-teal/30 rounded-3xl p-4 shadow-2xl flex items-center gap-4">
                            <div className="w-14 h-14 bg-sal-cyber-teal text-sal-gunmetal rounded-2xl flex items-center justify-center shadow-lg shrink-0">{getInstructionIcon(currentInstruction.instructions)}</div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest mb-1">A {currentInstruction.distance?.text || '---'}</p>
                                <div className="text-text-primary text-base font-bold leading-tight uppercase line-clamp-2">{getInstructionText(currentInstruction.instructions)}</div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                {(mapProvider === 'loading' || !config.googleMapsApiKey) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-card-bg z-20">
                        <div className="flex flex-col items-center gap-3"><Loader2 className="w-6 h-6 animate-spin text-sal-cyber-teal" /><span className="text-text-muted text-xs font-black uppercase tracking-widest">Iniciando Radar Tático...</span></div>
                    </div>
                )}
            </motion.div>
            <div className={`absolute right-4 z-40 flex flex-col gap-2 transition-all duration-500 ${isNavigatingInternal ? 'top-28' : 'top-4'}`}>
                <AnimatePresence>
                    {displayMode === 'recon' ? (
                        <motion.button type="button" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={() => setDisplayMode?.('tactical')} className="p-3 bg-sal-cyber-teal text-sal-gunmetal border-2 border-sal-gunmetal rounded-2xl shadow-xl font-black flex items-center gap-2 uppercase text-xs tracking-widest transition-all active:scale-95"><MapIcon className="w-5 h-5" /> Sair do RECON</motion.button>
                    ) : isNavigatingInternal ? (
                        <motion.button type="button" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={onStopInternalNav} className="p-3 bg-rose-600 text-badge-text border-2 border-sal-gunmetal rounded-2xl shadow-xl font-black flex items-center gap-2 uppercase text-xs tracking-widest transition-all active:scale-95"><XCircle className="w-5 h-5" /> Parar GPS</motion.button>
                    ) : (
                        <button type="button" onClick={() => setIsMapExpanded(!isMapExpanded)} className="p-3 bg-card-bg/85 backdrop-blur-md border border-border-dim rounded-2xl text-text-primary shadow-xl active:scale-90 transition-all">{isMapExpanded ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}</button>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};
